import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  FaSyncAlt, FaDownload, FaDatabase, FaSearch, 
  FaEnvelope, FaPhone, FaBuilding, FaServicestack, 
  FaRegCommentAlt, FaTimes, FaTimesCircle, FaCheckCircle, 
  FaUser, FaCalendarAlt 
} from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './ContactAdmin.css';

// Google Sheets Web App URL for Contact Us Form (read-only doGet connection)
const GOOGLE_SHEETS_CONTACT_URL = 'https://script.google.com/macros/s/AKfycbz8V5hlvIIf7c94SruBMRJK_sQ4IuoecdZ_7HRU8SJ6c0MBvZxtVTQul2dCRIPMlQNaxQ/exec';

// High-fidelity sandbox data for testing the UI
const MOCKUP_QUERIES = [
  {
    "Timestamp": "2026-05-22T08:34:12.124Z",
    "firstName": "Rajesh",
    "lastName": "Kumar",
    "companyName": "Kumar Logistics & Co",
    "mobileNumber": "+91 98412 34567",
    "email": "rajesh.kumar@kumarlogistics.in",
    "product": "Tally AWS",
    "message": "We want to migrate our existing on-premise Tally ERP 9 license to AWS TallyPrime Gold cloud setup for 8 branch offices. Please share pricing details."
  },
  {
    "Timestamp": "2026-05-21T16:12:45.654Z",
    "firstName": "Meera",
    "lastName": "Deshmukh",
    "companyName": "Apex Healthcare Ltd",
    "mobileNumber": "+91 87654 99887",
    "email": "m.deshmukh@apexhealth.org",
    "product": "Tally Customization",
    "message": "Looking for TDL customization to integrate prescription billing with TallyPrime Silver. We need a sales entry screen redesign."
  },
  {
    "Timestamp": "2026-05-21T10:22:01.345Z",
    "firstName": "John",
    "lastName": "Daniel",
    "companyName": "Innovate Software Inc",
    "mobileNumber": "+91 95006 12345",
    "email": "john.d@innovate.io",
    "product": "Web Application",
    "message": "We need an enterprise admin portal for logistics tracking. It should be built in React and integrated with automated notifications. Let's schedule a call."
  },
  {
    "Timestamp": "2026-05-20T14:45:12.789Z",
    "firstName": "Ananya",
    "lastName": "Pillai",
    "companyName": "Pillai Retail Chain",
    "mobileNumber": "+91 99445 11223",
    "email": "contact@pillairetails.com",
    "product": "Tallyprime Gold",
    "message": "We need to purchase a fresh TallyPrime Gold multi-user license. Please let us know if discount pricing is available for corporate bundles."
  },
  {
    "Timestamp": "2026-05-19T11:05:33.210Z",
    "firstName": "Vikram",
    "lastName": "Singh",
    "companyName": "V.S. Enterprises",
    "mobileNumber": "+91 91234 98765",
    "email": "vikram@vsenterprises.co.in",
    "product": "Consulting Services",
    "message": "Our company needs general security and backup audits for our network printer setup. Please share your availability next week."
  }
];

const ContactAdmin = () => {
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [productFilter, setProductFilter] = useState('all');
  const [selectedQuery, setSelectedQuery] = useState(null);
  const location = useLocation();
  const [hasError, setHasError] = useState(false);

  // Fetch candidates/queries from Google Sheets Web App
  const fetchQueries = async (forceDemo = false) => {
    setLoading(true);
    setHasError(false);

    if (forceDemo || !GOOGLE_SHEETS_CONTACT_URL) {
      // Sandbox fallback mode
      setTimeout(() => {
        setQueries(MOCKUP_QUERIES);
        setLoading(false);
        setIsDemoMode(true);
        toast.success("Loaded demo mode mock queries!");
      }, 800);
      return;
    }

    try {
      const response = await fetch(GOOGLE_SHEETS_CONTACT_URL);
      if (!response.ok) {
        throw new Error(`HTTP Error Status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error);
      }
      
      if (!Array.isArray(data)) {
        throw new Error("Invalid response format: Expected JSON Array");
      }

      // Format properties to normalize variations in column headers
      const formatted = data.map(item => ({
        Timestamp: item.Timestamp || item.timestamp || new Date().toISOString(),
        firstName: item.firstName || item.FirstName || item["First Name"] || "",
        lastName: item.lastName || item.LastName || item["Last Name"] || "",
        companyName: item.companyName || item.CompanyName || item["Company Name"] || item.company || "",
        mobileNumber: item.mobileNumber || item.MobileNumber || item["Mobile Number"] || item.Mobile || item.mobile || item.phone || item.contact || "",
        email: item.email || item.Email || item["Email Address"] || "",
        product: item.product || item.Product || item["Service"] || item.service || "",
        message: item.message || item.Message || ""
      }));

      setQueries(formatted);
      setIsDemoMode(false);
      toast.success("Synchronized contact data with live Google Sheet!");
    } catch (err) {
      console.error("Live fetch error:", err);
      setHasError(true);
      // Failover to Demo Sandbox
      setQueries(MOCKUP_QUERIES);
      setIsDemoMode(true);
      toast.warning("Failed to connect live. Failover to Sandbox simulation.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQueries();
  }, []);

  // Filter criteria logic
  const filteredQueries = queries.filter(q => {
    const searchString = `${q.firstName} ${q.lastName} ${q.companyName} ${q.email} ${q.mobileNumber} ${q.product} ${q.message}`.toLowerCase();
    const matchesSearch = searchString.includes(searchQuery.toLowerCase());

    let matchesProduct = true;
    if (productFilter !== 'all') {
      matchesProduct = q.product.toLowerCase().trim() === productFilter.toLowerCase().trim();
    }

    return matchesSearch && matchesProduct;
  });

  // Extract unique products/services for filtering dropdown
  const uniqueProducts = Array.from(
    new Set(queries.map(q => q.product).filter(p => p && p.trim() !== ''))
  );

  // Export data as CSV
  const exportToCSV = () => {
    if (filteredQueries.length === 0) {
      toast.error("No data available to export");
      return;
    }

    const headers = [
      "Timestamp", "First Name", "Last Name", "Company", "Mobile", "Email", 
      "Product/Service Requested", "Message"
    ];

    const safeCell = (val) => {
      if (val === null || val === undefined) return '""';
      return `"${String(val).replace(/"/g, '""')}"`;
    };

    const csvRows = [headers.join(",")];

    filteredQueries.forEach(q => {
      let dateString = q.Timestamp;
      try {
        const d = new Date(q.Timestamp);
        if (!isNaN(d.getTime())) {
          dateString = d.toLocaleString('en-IN');
        }
      } catch (e) {}

      const row = [
        safeCell(dateString),
        safeCell(q.firstName),
        safeCell(q.lastName),
        safeCell(q.companyName),
        safeCell(q.mobileNumber),
        safeCell(q.email),
        safeCell(q.product),
        safeCell(q.message)
      ];
      csvRows.push(row.join(","));
    });

    const blob = new Blob(["\ufeff" + csvRows.join("\n")], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `iMatrix_Contact_Queries_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("CSV Export downloaded successfully!");
  };

  // Metrics logic
  const totalCount = filteredQueries.length;
  const uniqueCompanies = Array.from(new Set(filteredQueries.map(q => q.companyName.toLowerCase().trim()).filter(Boolean))).length;
  const tallyRequests = filteredQueries.filter(q => q.product.toLowerCase().includes('tally')).length;

  return (
    <div className="admin-root">
      <ToastContainer position="top-right" autoClose={3000} theme="colored" />

      {/* ── Dashboard Header ── */}
      <header className="admin-header">
        <div className="admin-header-title">
          <span className="admin-badge">
            <span className={`live-dot ${!isDemoMode ? 'animate-pulse' : ''}`} style={{ backgroundColor: isDemoMode ? '#e2e8f0' : '#10b981' }} />
            {isDemoMode ? 'Sandbox Sandbox' : 'Live Connected'}
          </span>
          <h1>Contact Us Queries</h1>
          <p>Manage and audit real-time client inquiries and product consultations</p>
        </div>

        <div className="admin-actions">
          <button 
            className="refresh-btn" 
            onClick={() => fetchQueries(false)} 
            disabled={loading}
            title="Sync Live with Google Sheet"
          >
            <FaSyncAlt className={loading ? 'loading-spin' : ''} /> <span>Sync Live</span>
          </button>
          
          <button 
            className={`demo-btn ${isDemoMode ? 'active' : ''}`}
            onClick={() => fetchQueries(true)}
            title="Toggle Demo Mock Data"
          >
            <FaDatabase /> <span>Demo Mode</span>
          </button>

          <button 
            className="export-btn" 
            onClick={exportToCSV}
            title="Export database as CSV"
          >
            <FaDownload /> <span>Export CSV</span>
          </button>
        </div>
      </header>

      {/* ── Sub Navigation Links for Admin Dashboards ── */}
      <nav className="admin-subnav" style={{
        display: 'flex',
        gap: '20px',
        marginBottom: '30px',
        borderBottom: '2px solid var(--border, #e2e8f0)',
        paddingBottom: '12px'
      }}>
        <Link to="/admin/superadmin" style={{
          textDecoration: 'none',
          color: 'var(--text-secondary, #64748b)',
          fontWeight: '700',
          fontSize: '1.05rem',
          borderBottom: '3px solid transparent',
          paddingBottom: '10px',
          transition: 'all 0.2s'
        }}>
          Careers Portal
        </Link>
        <Link to="/admin/contactus" style={{
          textDecoration: 'none',
          color: 'var(--brand, #6b7a52)',
          fontWeight: '700',
          fontSize: '1.05rem',
          borderBottom: '3px solid var(--brand, #6b7a52)',
          paddingBottom: '10px',
          transition: 'all 0.2s'
        }}>
          Contact Messages
        </Link>
        <Link to="/admin/pagehits" style={{
          textDecoration: 'none',
          color: 'var(--text-secondary, #64748b)',
          fontWeight: '700',
          fontSize: '1.05rem',
          borderBottom: '3px solid transparent',
          paddingBottom: '10px',
          transition: 'all 0.2s'
        }}>
          Page Hits Tracker
        </Link>
      </nav>

      {/* ── Key Metrics Cards ── */}
      <section className="metrics-dashboard">
        <div className="metric-card">
          <div className="metric-icon-wrap user-icon">
            <FaEnvelope />
          </div>
          <div className="metric-info">
            <span className="metric-label">Total Queries</span>
            <h2 className="metric-value">{totalCount}</h2>
            <span className="metric-trend green">Customer submissions</span>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon-wrap experience-icon">
            <FaBuilding />
          </div>
          <div className="metric-info">
            <span className="metric-label">Unique Companies</span>
            <h2 className="metric-value">{uniqueCompanies}</h2>
            <span className="metric-trend blue">Distinct corporate leads</span>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon-wrap notice-icon">
            <FaServicestack />
          </div>
          <div className="metric-info">
            <span className="metric-label">Tally Solutions Leads</span>
            <h2 className="metric-value">{tallyRequests}</h2>
            <span className="metric-trend gold">TallyPrime / TDL queries</span>
          </div>
        </div>
      </section>

      {/* ── Filter Controls Section ── */}
      <section className="filter-controls-card">
        <div className="search-box-wrap">
          <FaSearch className="search-icon" />
          <input 
            type="text" 
            placeholder="Search queries by name, company, email or details..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="clear-search" onClick={() => setSearchQuery('')}>
              <FaTimesCircle />
            </button>
          )}
        </div>

        <div className="filters-dropdowns-wrap">
          <div className="filter-select-group">
            <label>Service Requested</label>
            <select 
              value={productFilter} 
              onChange={(e) => setProductFilter(e.target.value)}
            >
              <option value="all">All Services</option>
              {uniqueProducts.map((prod, idx) => (
                <option key={idx} value={prod}>{prod}</option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {/* ── Database Table Grid ── */}
      <main className="candidates-table-card">
        {loading ? (
          <div className="table-loading-state">
            <FaSyncAlt className="loading-spin" />
            <p>Syncing contact query records...</p>
          </div>
        ) : filteredQueries.length === 0 ? (
          <div className="table-empty-state">
            <FaTimesCircle />
            <h3>No Queries Found</h3>
            <p>Try modifying your search query or service filter</p>
          </div>
        ) : (
          <div className="table-responsive-wrapper">
            <table className="candidates-table">
              <thead>
                <tr>
                  <th>Client / Organization</th>
                  <th>Contact Info</th>
                  <th>Service Requested</th>
                  <th>Message Snippet</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredQueries.map((q, idx) => (
                  <tr key={idx} onClick={() => setSelectedQuery(q)}>
                    <td>
                      <div className="candidate-profile-cell">
                        <div className="candidate-avatar">
                          {q.firstName[0] || '?'}{q.lastName[0] || '?'}
                        </div>
                        <div>
                          <h4 className="candidate-name">{q.firstName} {q.lastName}</h4>
                          <span className="candidate-timestamp">
                            {new Date(q.Timestamp).toLocaleDateString('en-IN', {
                              day: 'numeric', month: 'short', year: 'numeric',
                              hour: '2-digit', minute: '2-digit'
                            })}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="contact-details-cell">
                        <div className="detail-row">
                          <FaBuilding /> <span>{q.companyName || 'N/A'}</span>
                        </div>
                        <div className="detail-row">
                          <FaEnvelope /> <span>{q.email}</span>
                        </div>
                        <div className="detail-row">
                          <FaPhone /> <span>{q.mobileNumber}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="exp-badge">
                        {q.product || 'General Inquiry'}
                      </span>
                    </td>
                    <td>
                      <p style={{ 
                        margin: 0, 
                        maxWidth: '300px', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        color: 'var(--text-secondary, #64748b)',
                        fontSize: '0.9rem'
                      }}>
                        {q.message}
                      </p>
                    </td>
                    <td className="text-center" onClick={(e) => e.stopPropagation()}>
                      <button 
                        className="view-details-btn" 
                        onClick={() => setSelectedQuery(q)}
                      >
                        Review Message
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* ── Query Detail Modal Drawer ── */}
      {selectedQuery && (
        <div className="modal-overlay" onClick={() => setSelectedQuery(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <header className="modal-header">
              <div className="modal-title-wrap">
                <div className="candidate-avatar large">
                  {selectedQuery.firstName[0] || '?'}{selectedQuery.lastName[0] || '?'}
                </div>
                <div>
                  <h2>{selectedQuery.firstName} {selectedQuery.lastName}</h2>
                  <p className="modal-subtitle">Received on {new Date(selectedQuery.Timestamp).toLocaleString('en-IN')}</p>
                </div>
              </div>
              <button className="close-modal-btn" onClick={() => setSelectedQuery(null)}>
                <FaTimes />
              </button>
            </header>

            <div className="modal-body-content">
              {/* Contact Profile Section */}
              <div className="modal-info-section">
                <h3><FaUser /> Profile Details</h3>
                <div className="modal-grid-fields">
                  <div className="field-group">
                    <span className="field-label">Company Name</span>
                    <span className="field-value">{selectedQuery.companyName || 'N/A'}</span>
                  </div>
                  <div className="field-group">
                    <span className="field-label">Product / Service</span>
                    <span className="field-value">{selectedQuery.product || 'General Inquiry'}</span>
                  </div>
                  <div className="field-group">
                    <span className="field-label">Email Address</span>
                    <a href={`mailto:${selectedQuery.email}`} className="field-value link">{selectedQuery.email}</a>
                  </div>
                  <div className="field-group">
                    <span className="field-label">Mobile Number</span>
                    <a href={`tel:${selectedQuery.mobileNumber}`} className="field-value link">{selectedQuery.mobileNumber}</a>
                  </div>
                </div>
              </div>

              {/* Message Details Section */}
              <div className="modal-info-section full-width">
                <h3><FaRegCommentAlt /> Client Inquiry Message</h3>
                <div style={{ 
                  background: 'var(--bg-subtle, #f1f5f9)', 
                  border: '1px solid var(--border, #e2e8f0)', 
                  borderRadius: '16px', 
                  padding: '24px',
                  color: 'var(--text-primary, #1e293b)',
                  lineHeight: '1.6',
                  fontSize: '1rem',
                  fontWeight: '500',
                  whiteSpace: 'pre-wrap'
                }}>
                  {selectedQuery.message}
                </div>
              </div>
            </div>

            <footer className="modal-footer-actions">
              <a href={`mailto:${selectedQuery.email}?subject=Regarding your inquiry on iMatrix`} className="contact-candidate-btn email">
                <FaEnvelope /> Email Client
              </a>
              <a href={`tel:${selectedQuery.mobileNumber}`} className="contact-candidate-btn call">
                <FaPhone /> Call Client
              </a>
              <button className="dismiss-btn" onClick={() => setSelectedQuery(null)}>Close</button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactAdmin;
